// Campaign / referral / employee-referral attribution tracking.
// Captures ?campaign=, ?refer=, ?employee_detail= and any ?utm_* params from the URL,
// mirrors them into cookies (15-day persistence, matching the legacy site's behavior)
// so they survive full page navigations and are available to submit with any form.

const COOKIE_DAYS = 15;
const TRACKED_QUERY_KEYS = ['campaign', 'refer', 'employee_detail'];

// Ad-platform click IDs, mirroring the legacy CodeIgniter site's CLICK_IDS list
// (plus gbraid, its wbraid counterpart for Google Ads' iOS app-conversion clicks).
// Order doubles as priority when more than one click ID lands on the same URL.
const CLICK_ID_SOURCES = [
    ['fbclid', 'Meta/Facebook Ads'],
    ['gclid', 'Google Ads'],
    ['dclid', 'Google Marketing Platform / Display & Video 360 (DoubleClick)'],
    ['msclkid', 'Microsoft Advertising (Bing Ads)'],
    ['li_fat_id', 'LinkedIn Ads'],
    ['ttclid', 'TikTok Ads'],
    ['twclid', 'X (Twitter) Ads'],
    ['ko_click_id', 'Kakao Ads'],
    ['wbraid', 'Google Ads (iOS web-to-app conversions)'],
    ['gbraid', 'Google Ads (iOS app conversions)'],
];
const CLICK_ID_KEYS = CLICK_ID_SOURCES.map(([key]) => key);
const CLICK_ID_SOURCE_MAP = Object.fromEntries(CLICK_ID_SOURCES);

TRACKED_QUERY_KEYS.push(...CLICK_ID_KEYS);

// Resolves the human-readable "Source" for a set of query params: a known ad
// click ID wins first (most specific attribution), falling back to the raw
// utm_source value (e.g. utm_source=ymii_va -> "ymii_va").
const resolveSource = (params) => {
    for (const key of CLICK_ID_KEYS) {
        const value = params.get ? params.get(key) : params[key];
        if (value) return CLICK_ID_SOURCE_MAP[key];
    }
    const utmSource = params.get ? params.get('utm_source') : params.utm_source;
    return utmSource || null;
};

const setCookie = (name, value, days) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/`;
};

const getCookie = (name) => {
    if (typeof document === 'undefined' || !document.cookie) return null;
    const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
};

const getCookieNames = () => {
    if (typeof document === 'undefined' || !document.cookie) return [];
    return document.cookie
        .split(';')
        .map((entry) => entry.trim().split('=')[0])
        .filter(Boolean);
};

// Reads the current URL's query string and stores any tracking params found into cookies.
// Existing cookie values are only overwritten when the param is present in the URL again,
// so attribution set on an earlier page persists as the visitor navigates onward.
export const captureTrackingParams = (search) => {
    if (!search) return;
    const params = new URLSearchParams(search);
    let matchedAny = false;

    TRACKED_QUERY_KEYS.forEach((key) => {
        const value = params.get(key);
        if (value) {
            setCookie(key, value, COOKIE_DAYS);
            matchedAny = true;
        }
    });

    params.forEach((value, key) => {
        if (key.toLowerCase().startsWith('utm_') && value) {
            setCookie(key, value, COOKIE_DAYS);
            matchedAny = true;
        }
    });

    if (matchedAny) {
        setCookie('campaign_query_string', search, COOKIE_DAYS);

        const source = resolveSource(params);
        if (source) setCookie('source', source, COOKIE_DAYS);

        if (typeof window !== 'undefined') {
            setCookie('landing_page_url', window.location.href, COOKIE_DAYS);
        }
    }
};

// Returns everything currently tracked (from cookies), keyed by query-param name.
export const getTrackingParams = () => {
    const data = {};

    TRACKED_QUERY_KEYS.forEach((key) => {
        const value = getCookie(key);
        if (value) data[key] = value;
    });

    getCookieNames()
        .filter((name) => name.toLowerCase().startsWith('utm_'))
        .forEach((name) => {
            const value = getCookie(name);
            if (value) data[name] = value;
        });

    const queryString = getCookie('campaign_query_string');
    if (queryString) data.campaign_query_string = queryString;

    const source = getCookie('source');
    if (source) data.source = source;

    const landingPageUrl = getCookie('landing_page_url');
    if (landingPageUrl) data.landing_page_url = landingPageUrl;

    return data;
};

// Maps the tracked cookie values onto the field names forms should submit them under.
export const getTrackingFormFields = () => {
    const tracking = getTrackingParams();
    const fields = {};

    if (tracking.campaign) fields.campaign_code = tracking.campaign;
    if (tracking.refer) fields.employee_code = tracking.refer;
    if (tracking.employee_detail) fields.getfriday_user_detail = tracking.employee_detail;
    if (tracking.campaign_query_string) fields.campaign_query_string = tracking.campaign_query_string;
    if (tracking.source) fields.source = tracking.source;
    if (tracking.landing_page_url) fields.landing_page_url = tracking.landing_page_url;

    Object.keys(tracking).forEach((key) => {
        if (key.toLowerCase().startsWith('utm_') || CLICK_ID_KEYS.includes(key)) fields[key] = tracking[key];
    });

    return fields;
};

const CLIENT_REFERENCE_SESSION_KEY = 'gf_client_reference';

// Captures the HTTP referrer once per browser session, mirroring the legacy site's
// `$_SERVER['HTTP_REFERER']` capture (used for affiliate-marketer attribution).
// document.referrer reflects the referrer of the original page load, so this is
// seeded once and reused across SPA navigations within the same tab session.
export const getClientReference = () => {
    try {
        const existing = window.sessionStorage.getItem(CLIENT_REFERENCE_SESSION_KEY);
        if (existing !== null) return existing;

        const referrer = document.referrer || '';
        window.sessionStorage.setItem(CLIENT_REFERENCE_SESSION_KEY, referrer);
        return referrer;
    } catch {
        return document.referrer || '';
    }
};

// Appends the currently tracked campaign/referral/utm params onto an outbound URL
// (used for links that leave the SPA, e.g. cross-domain signup/login links, where
// cookies set on getfriday.com can't be read by myaccount.getfriday.com).
export const appendTrackingParams = (url) => {
    const SYNTHETIC_KEYS = ['campaign_query_string', 'source', 'landing_page_url'];
    const tracking = getTrackingParams();
    const keys = Object.keys(tracking).filter((key) => !SYNTHETIC_KEYS.includes(key));
    if (!keys.length) return url;

    const [base, hash] = url.split('#');
    const separator = base.includes('?') ? '&' : '?';
    const query = keys.map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(tracking[key])}`).join('&');

    return `${base}${separator}${query}${hash ? `#${hash}` : ''}`;
};
